import React, { useState } from 'react';
import { Navbar } from '../components/layouts/Navbar';
import { PlannerSidebar } from '../components/layouts/PlannerSidebar';
import { ApoliceTable } from '../components/apolice/ApoliceTable';
import { ApoliceModal } from '../components/apolice/ApoliceModal';
import { ApoliceFormModal } from '../components/apolice/ApoliceFormModal';
import { ApoliceCreationWizard } from '../components/apolice/ApoliceCreationWizard';
import { documentoService } from '../services/entitiesServices';
import type { ApoliceData } from '../services/apoliceService';
import { useNotifier } from '../components/ui/Notifier';


export const Apolice: React.FC = () => {
  const notifier = useNotifier();
  const [selectedApolice, setSelectedApolice] = useState<ApoliceData | undefined>();
  const [showModal, setShowModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showCreationWizard, setShowCreationWizard] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [navigatorFn, setNavigatorFn] = useState<((currentId?: string) => Promise<ApoliceData | undefined>) | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleCreateNew = () => {
    setShowCreationWizard(true);
  };

  const handleWizardSuccess = async (apoliceId: string) => {
    try {
      // Load the created apólice
      const apoliceResp = await documentoService.getById(apoliceId);
      const apolice = apoliceResp && (apoliceResp as any).data ? (apoliceResp as any).data : (apoliceResp as any);

      // Open it in form modal for further editing (ensure we pass raw data)
      setSelectedApolice(apolice);
      setIsEditing(true);
      setShowFormModal(true);
      
      // Refresh the table
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Erro ao carregar apólice criada:', error);
      notifier.show({ message: 'Apólice criada com sucesso, mas ocorreu um erro ao carregá-la para edição.', severity: 'warning' });
      setRefreshTrigger(prev => prev + 1);
    }
  };

  const handleEdit = (apolice: ApoliceData) => {
    setSelectedApolice(apolice);
    setIsEditing(true);
    setShowFormModal(true);
  };

  const handleView = (apolice: ApoliceData) => {
    setSelectedApolice(apolice);
    setModalViewReadonly(true);
    setShowModal(true);
    setIsEditing(false);
    setShowFormModal(false);
  };

  const [modalViewReadonly, setModalViewReadonly] = useState(false);

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedApolice(undefined);
    setModalViewReadonly(false);
  };

  const handleFormModalClose = () => {
    setShowFormModal(false);
    setSelectedApolice(undefined);
    setIsEditing(false);
  };

  const handleDelete = async (apolice: ApoliceData) => {
    if (!apolice.id) {
      notifier.show({ message: 'ID da apólice não encontrado', severity: 'error' });
      return;
    }

    try {
      await documentoService.delete(apolice.id);
      notifier.show({ message: 'Apólice deletada com sucesso!', severity: 'success' });
      setRefreshTrigger(prev => prev + 1);
      setShowModal(false);
    } catch (err) {
      notifier.show({ 
        message: err instanceof Error ? err.message : 'Erro ao deletar apólice', 
        severity: 'error' 
      });
    }
  };

  const handleFormSave = (apoliceData: ApoliceData) => {
    // Aqui você implementaria a lógica de salvamento
    // Por exemplo, chamaria o apoliceService.save(apoliceData)
    setShowFormModal(false);
    setSelectedApolice(undefined);
    setIsEditing(false);
  };

  const handleFormUpdated = (apoliceData: ApoliceData) => {
    // Called when the form saved but the modal should remain open.
    // Trigger list refresh and update the currently selected apólice data.
    setRefreshTrigger(prev => prev + 1);
    setSelectedApolice(apoliceData);
    // keep modal open and editing state as-is
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F4EFFA' }}>
      <Navbar />
      <div className="flex p-6 gap-3">
        <div className="flex-1">
          {/* Header */}
          <div className="flex justify-between items-center mb-2">
            <div>
              <h1 className="text-xs font-semibold" style={{ color: '#3d2b56' }}>
                Gestão de Apólices
              </h1>
              <p className="text-xs mt-1" style={{ color: '#9E9E9E' }}>
                Gerencie as apólices e propostas do sistema
              </p>
            </div>
            
            <button
              onClick={handleCreateNew}
              className="px-3 py-1.5 text-xs font-medium text-white rounded-md hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-opacity"
              style={{ backgroundColor: '#7A61B2' }}
            >
              + Nova Apólice
            </button>
          </div>

          {/* Conteúdo principal */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <ApoliceTable 
                onEdit={handleEdit}
                onView={handleView}
                onDelete={handleDelete}
                  onRefresh={() => {
                    // Função para atualizar a lista quando necessário
                  }}
                  activeId={selectedApolice?.id || null}
                  refreshTrigger={refreshTrigger}
                  onRegisterNavigator={(fn) => setNavigatorFn(() => fn)}
              />
          </div>
        </div>
        
        <div className="hidden lg:block">
          <PlannerSidebar />
        </div>
      </div>

      {/* Modal de Visualização */}
      <ApoliceModal
        apolice={selectedApolice}
        isOpen={showModal}
        onClose={handleModalClose}
        onEdit={(apolice) => {
          setShowModal(false);
          handleEdit(apolice);
        }}
        onDelete={handleDelete}
        showRepassesTab={!modalViewReadonly}
      />

      {/* Modal de Formulário */}
      <ApoliceFormModal
        apolice={selectedApolice}
        isOpen={showFormModal}
        isEditing={isEditing}
        onClose={handleFormModalClose}
        onSave={handleFormSave}
        onUpdated={handleFormUpdated}
        onNextRequested={async (currentId?: string) => {
          if (navigatorFn) {
            const next = await navigatorFn(currentId);
            if (next) {
              setSelectedApolice(next);
            }
            return next;
          }
          return undefined;
        }}
      />

      {/* Wizard de Criação */}
      <ApoliceCreationWizard
        isOpen={showCreationWizard}
        onClose={() => setShowCreationWizard(false)}
        onSuccess={handleWizardSuccess}
      />
    </div>
  );
};